import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Filter, GqlWatchResponse, IAppDataTablePageData } from '../models/common.model';
import { GqlQueryBuilderService } from './gql-query-builder.service';
import { PagerService } from './pager-service.service';
import { DatatableModel } from '../standalone-packages/ecap3/app-platform-datatable';
import { EcapGql } from '../standalone-packages/ecap3/app-platform-graphql';

@Injectable({
    providedIn: 'root'
})
export class GqlQueryCommandService {
    constructor(
        private gqlQueryBuilderService: GqlQueryBuilderService,
        private GQL: EcapGql,
        private pagerService: PagerService
    ) {}

    /**
     * @param entityName GQL EntityName
     * @param fields fields you want to get
     * @param filters Self explanatory
     * @param orderBy Field Name: -1/1, 1 to sort in ascending order, -1 to sort in descending order
     * @param pageNumber Which page you want
     * @param pageSize Number of data you want
     */

    gqlWatchQuery<T>(
        entityName: string,
        fields: (string | Object)[],
        filters: Filter[],
        orderBy = 'CreateDate: -1',
        pageNumber = 0,
        pageSize = 10
    ): Observable<GqlWatchResponse<T>> {
        return this.GQL.watchQuery<any>({
            query: this.gqlQueryBuilderService.prepareQuery(entityName, fields, filters, orderBy, pageNumber, pageSize),
            errorPolicy: 'all'
        }).valueChanges.pipe(
            take(1),
            map(
                response => {
                    if (response.data && response.data[`${entityName}s`]) {
                        return new GqlWatchResponse(
                            (response.data[`${entityName}s`].Data || []) as T[],
                            response.data[`${entityName}s`].TotalCount
                        );
                    }
                    console.error(`There was an error in Fetching  ${entityName}`);
                    console.error(response);
                    return new GqlWatchResponse();
                },
                error => {
                    console.error(error);
                    return new GqlWatchResponse();
                }
            )
        );
    }

    /**
     * @param entityName: GQL EntityName
     * @param fields: fields you want to get
     * @param filters: Self explanatory
     * @param orderBy: Field Name: -1/1, 1 to sort in ascending order, -1 to sort in descending order
     */

    gqlWatchQueryGetAll<T>(
        entityName: string,
        fields: string[],
        filters: Filter[],
        orderBy = 'CreateDate: -1'
    ): Observable<GqlWatchResponse<T>> {
        const pageSize = 100;
        return new Observable(observable => {
            this.gqlWatchQuery<T>(entityName, fields, filters, orderBy, 0, pageSize).subscribe(response => {
                if (!response) {
                    observable.next(new GqlWatchResponse<T>());
                    observable.complete();
                } else {
                    const totalPages = Math.ceil(response.TotalCount / pageSize);
                    const calls: Observable<GqlWatchResponse<T>>[] = [of(response)];
                    for (let i = 1; i < totalPages; i++) {
                        calls.push(this.gqlWatchQuery<T>(entityName, fields, filters, orderBy, i, pageSize));
                    }
                    combineLatest(calls).subscribe(responseList => {
                        const data: T[] = [];
                        responseList.forEach(result => {
                            data.push(...result.Data);
                        });
                        observable.next({
                            Data: data,
                            TotalCount: response.TotalCount
                        });
                        observable.complete();
                    });
                }
            });
        });
    }

    /**
     * @param entityName: GQL EntityName
     * @param fields: fields you want to get
     * @param filters: Self explanatory
     * @param orderBy: Field Name: -1/1, 1 to sort in ascending order, -1 to sort in descending order
     * @param datatableModel: DatatableModel you want to use in AppDataTable
     */

    gqlWatchQueryWithDatatableModel<T>(
        entityName: string,
        fields: string[],
        filters: any[],
        orderBy: string,
        datatableModel: DatatableModel<T>
    ): Observable<IAppDataTablePageData<T>> {
        return new Observable<IAppDataTablePageData<T>>(obs => {
            this.gqlWatchQuery<T>(
                entityName,
                fields,
                filters,
                orderBy,
                datatableModel.CurrentPageNumber,
                datatableModel.PageSize
            ).subscribe(response => {
                let getDataCall = of(this.parseGQLDataTableResponseData<T>(new GqlWatchResponse<T>(), datatableModel));
                if (response) {
                    if (response.TotalCount !== 0) {
                        if (response.Data.length > 0) {
                            getDataCall = of(this.parseGQLDataTableResponseData<T>(response, datatableModel));
                        } else {
                            datatableModel.CurrentPageNumber = 0;
                            getDataCall = this.gqlWatchQueryWithDatatableModel<T>(
                                entityName,
                                fields,
                                filters,
                                orderBy,
                                datatableModel
                            );
                        }
                    }
                } else {
                    console.error(response);
                }
                getDataCall.subscribe(response => {
                    obs.next(response);
                    obs.complete();
                });
            });
        });
    }

    parseGQLDataTableResponseData<T>(
        response: GqlWatchResponse<T>,
        dataTableModel: DatatableModel<T>
    ): IAppDataTablePageData<T> {
        const results = response.Data;
        const totalRecords = response.TotalCount;
        const dataStore = { data: [] as T[], pager: {} } as IAppDataTablePageData<T>;
        dataStore.data = results;
        dataStore.pager = this.pagerService.getPager(
            totalRecords,
            dataTableModel.CurrentPageNumber,
            dataTableModel.PageSize
        );
        return dataStore;
    }

    gqlInsert(entityName: string, model: any): Observable<any> {
        return this.GQL.mutate({
            mutation: this.gqlQueryBuilderService.prepareInsertModel(entityName, model)
        });
    }

    gqlUpdate(entityName: string, model: any, itemId?: string): Observable<any> {
        if (!(model.ItemId || itemId)) {
            throw new Error(`No ItemId found for ${entityName} Model`);
        }
        return this.GQL.mutate({
            mutation: this.gqlQueryBuilderService.prepareUpdateModel(entityName, model, model.ItemId || itemId)
        });
    }

    gqlDelete(entityName: string, itemId: string): Observable<any> {
        if (!itemId) {
            throw new Error(`No ItemId found for ${entityName} Model`);
        }
        return this.GQL.mutate({
            mutation: this.gqlQueryBuilderService.prepareDeleteModel(entityName, itemId)
        });
    }
    gqlAggregateWatchQuery(aggregateQuery: any[]) {
        return this.GQL.watchQuery<any>({
            query: this.gqlQueryBuilderService.aggregateMultipleQuery(aggregateQuery)
        }).valueChanges;
    }
}
